import React from "react";
import { Link } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import "../css/terms.scss";

function Terms() {
  return (
    <div className="terms legal-page">
      <Header />
      <main id="MainContent">
        <article id="cms-content" aria-labelledby="terms-title">
          <div>
            <p className="legal-page__eyebrow">Legal &amp; privacy</p>
            <h1 id="terms-title">Terms &amp; Conditions</h1>
            <p className="legal-page__updated">
              <strong>Last updated: October 16, 2026</strong>
            </p>
            <p className="legal-page__intro">
              These Terms &amp; Conditions govern access to the averlynxstore
              website and purchases made through it. Please read them with the
              policies linked in our Policy Center before placing an order.
            </p>

            <section className="legal-page__section">
              <h2>1. Agreement and mandatory rights</h2>
              <p>
                By using the website, creating an account, or submitting an
                order, you agree to these terms to the extent permitted by law.
                If you do not agree, do not use the service or place an order.
              </p>
              <p>
                Nothing in these terms excludes, restricts, or replaces a
                consumer, privacy, product-safety, or other right that applicable
                law does not allow the parties to waive. If there is a conflict,
                that mandatory law controls.
              </p>
            </section>

            <section className="legal-page__section">
              <h2>2. Eligibility and accounts</h2>
              <p>
                You must have legal capacity to enter the purchase contract. A
                minor may use the store only with the involvement and permission
                of a parent or guardian as required by law.
              </p>
              <p>
                If account features are available, provide accurate information,
                protect your credentials, and notify us promptly of suspected
                unauthorized access. You are responsible for activity you
                authorize, but not activity imposed on you by fraud or a security
                failure for which the law makes another party responsible.
              </p>
            </section>

            <section className="legal-page__section">
              <h2>3. Product information and availability</h2>
              <p>
                We aim to display accurate descriptions, images, sizes,
                materials, colors, prices, and availability. Screens, lighting,
                natural materials, and ordinary production variation can cause
                minor differences. Material inaccuracies remain subject to the
                remedies required by law.
              </p>
              <p>
                Products, colors, sizes, purchase limits, and availability may
                change. A product shown online is not reserved until the order is
                accepted. Review our{" "}
                <Link to="/policies/product-information-care">
                  Product Information &amp; Care Policy
                </Link>{" "}
                and <Link to="/policies/sizing-fit">Sizing &amp; Fit Guidance</Link>.
              </p>
            </section>

            <section className="legal-page__section">
              <h2>4. Prices, taxes, and promotions</h2>
              <p>
                The price, currency, shipping charge, and any tax collected by
                us are shown before you submit the order. Your bank or payment
                provider may charge separate conversion or account fees. Import
                duties or charges not collected at checkout may be payable at
                destination.
              </p>
              <p>
                Promotions are subject to their stated eligibility, dates,
                product exclusions, limits, and code instructions. See the{" "}
                <Link to="/policies/promotions-pricing">
                  Promotions, Discounts &amp; Pricing Policy
                </Link>
                .
              </p>
            </section>

            <section className="legal-page__section">
              <h2>5. Orders and contract formation</h2>
              <p>
                Your order is an offer to purchase. An automated receipt confirms
                that the request reached us but does not necessarily accept it.
                Unless local law provides otherwise, acceptance occurs when we
                send an acceptance or shipment confirmation or begin fulfillment.
              </p>
              <p>
                We may reject or cancel an order for a legitimate reason such as
                unavailable stock, failed payment, an obvious error, an
                undeliverable address, a legal restriction, suspected fraud, or
                abuse of purchase limits. We will give notice and refund any
                amount collected for a cancelled item. See the{" "}
                <Link to="/policies/orders-cancellations">
                  Orders, Changes &amp; Cancellations Policy
                </Link>
                .
              </p>
            </section>

            <section className="legal-page__section">
              <h2>6. Payment</h2>
              <p>
                Payment methods are those displayed at checkout. You confirm
                that you are authorized to use the chosen method and that the
                billing information is accurate. Payment providers may perform
                authorization, fraud, sanctions, and identity checks under their
                own terms and notices.
              </p>
              <p>
                See the <Link to="/policies/payment-billing">Payment &amp; Billing Policy</Link>{" "}
                for authorizations, failed payments, duplicate charges, and
                refund processing.
              </p>
            </section>

            <section className="legal-page__section">
              <h2>7. Shipping, delivery, and risk</h2>
              <p>
                Available services, charges, and estimates are presented at
                checkout or in our <Link to="/shipping">Shipping &amp; Delivery Policy</Link>.
                Estimates are not guaranteed unless expressly stated. You are
                responsible for providing an accurate deliverable address and
                for cooperating with reasonable carrier requirements.
              </p>
              <p>
                Risk of loss transfers when you or a person you designate takes
                physical possession, except where applicable law provides a
                different rule. This does not cover a carrier independently
                selected and arranged by you where the law treats risk
                differently.
              </p>
            </section>

            <section className="legal-page__section">
              <h2>8. Cancellations, returns, and refunds</h2>
              <p>
                Operational cancellation, return, exchange, and refund rules are
                described in our <Link to="/returns">Returns &amp; Exchanges Policy</Link>.
                Those rules do not limit a statutory withdrawal period, legal
                guarantee, or remedy for goods that are faulty, unsafe,
                misdescribed, or otherwise non-conforming.
              </p>
            </section>

            <section className="legal-page__section">
              <h2>9. Gift cards, credits, and pre-orders</h2>
              <p>
                If offered, gift cards, store credits, pre-orders, or backorders
                are subject to the disclosures shown before purchase and the
                applicable pages in our Policy Center. Any expiration,
                cancellation, or refund limitation applies only to the extent
                permitted by law.
              </p>
            </section>

            <section className="legal-page__section">
              <h2>10. Website license and intellectual property</h2>
              <p>
                We grant you a limited, revocable, non-exclusive,
                non-transferable right to access the website for personal,
                lawful shopping and information purposes. No ownership right is
                transferred.
              </p>
              <p>
                Original averlynxstore content and branding are owned by us, or
                content is used under permission or another lawful basis.
                Third-party names and marks remain the property of their
                respective owners and do not imply affiliation. See our{" "}
                <Link to="/policies/intellectual-property">
                  Intellectual Property &amp; Rights Policy
                </Link>
                .
              </p>
            </section>

            <section className="legal-page__section">
              <h2>11. Reviews and other submissions</h2>
              <p>
                You retain ownership of content you submit. When submission
                features are available, you give us the limited permissions
                described in the{" "}
                <Link to="/policies/reviews-user-content">
                  Reviews &amp; User Content Policy
                </Link>
                . You must have the rights needed to submit that content and may
                not include unlawful material or another person&apos;s private data
                without authority.
              </p>
            </section>

            <section className="legal-page__section">
              <h2>12. Acceptable use</h2>
              <p>
                Do not misuse the website, compromise security, submit
                fraudulent information, violate rights, interfere with service,
                evade limits, introduce harmful code, or use automation in a way
                that burdens the service or copies protected content. The full{" "}
                <Link to="/policies/acceptable-use">Acceptable Use Policy</Link>{" "}
                applies.
              </p>
            </section>

            <section className="legal-page__section">
              <h2>13. Third-party services and links</h2>
              <p>
                Independent payment providers, carriers, social platforms, and
                linked sites may have their own terms. We are not responsible
                for an unrelated party&apos;s content or acts, but this clause does
                not excuse us from responsibility the law places on us for
                providers acting on our behalf.
              </p>
            </section>

            <section className="legal-page__section">
              <h2>14. Product warranties and safety</h2>
              <p>
                Products are covered by mandatory legal guarantees and implied
                warranties that apply to the sale. Any voluntary warranty is
                additional and will be stated separately. Review the{" "}
                <Link to="/policies/warranty-defects">
                  Warranty &amp; Defective Products Policy
                </Link>{" "}
                and the{" "}
                <Link to="/policies/product-safety-recalls">
                  Product Safety &amp; Recall Policy
                </Link>
                .
              </p>
            </section>

            <section className="legal-page__section">
              <h2>15. Service availability and disclaimers</h2>
              <p>
                We do not promise that the website will always be uninterrupted,
                error-free, or compatible with every device. We may maintain,
                change, or withdraw non-essential features. To the maximum
                extent permitted by law, implied terms relating solely to the
                free website service are excluded. This does not exclude product
                obligations, express promises, or mandatory rights.
              </p>
            </section>

            <section className="legal-page__section">
              <h2>16. Liability</h2>
              <p>
                Nothing limits liability that cannot lawfully be limited,
                including liability for fraud, fraudulent misrepresentation, or
                death or personal injury caused by negligence where applicable.
              </p>
              <p>
                Subject to that rule, neither party is responsible for remote or
                unforeseeable loss that was not a natural result of the breach.
                If you are a consumer, we do not exclude responsibility for loss
                that was reasonably foreseeable when the contract was made. The
                website and products are for personal use unless a business sale
                is agreed in writing, so business-only losses such as lost profit
                are not recoverable from a consumer transaction to the extent
                the law permits.
              </p>
            </section>

            <section className="legal-page__section">
              <h2>17. Events outside reasonable control</h2>
              <p>
                A party is not responsible for delay caused by an event it could
                not reasonably control, but must take reasonable steps to reduce
                the effect and resume performance. This does not remove your
                right to cancel or receive a refund when applicable law provides
                one because of substantial delay or non-performance.
              </p>
            </section>

            <section className="legal-page__section">
              <h2>18. Suspension and termination</h2>
              <p>
                We may suspend access or cancel unfulfilled orders when
                reasonably necessary for security, fraud prevention, legal
                compliance, or a material breach of these terms. Where
                appropriate, we will give notice and an opportunity to address
                the issue. Terms intended to survive—such as payment, rights,
                liability, and dispute provisions—continue to apply.
              </p>
            </section>

            <section className="legal-page__section">
              <h2>19. Governing law and disputes</h2>
              <p>
                These terms are governed by the law applicable to the operator
                of averlynxstore, without depriving a consumer of mandatory
                protections or courts available under the law of the
                consumer&apos;s habitual residence. Before formal proceedings, you
                may contact us under the{" "}
                <Link to="/policies/complaints-disputes">
                  Complaints &amp; Dispute Resolution Policy
                </Link>
                , but you are not required to do so when law gives you an
                immediate remedy.
              </p>
            </section>

            <section className="legal-page__section">
              <h2>20. Changes, severability, and transfer</h2>
              <p>
                We may update these terms prospectively by posting a revised
                date and giving additional notice where required. The terms in
                effect when an order was accepted continue to govern that order
                unless a lawful change is agreed.
              </p>
              <p>
                If a provision is unenforceable, it will be narrowed or removed
                only as needed, and the rest remains effective. We may transfer
                our rights and duties as part of a legitimate business transfer
                if your rights are not reduced. You may transfer a right where
                the law permits or with our consent.
              </p>
            </section>

            <section className="legal-page__section">
              <h2>21. Contact</h2>
              <p>
                Questions about these terms may be sent to{" "}
                <a href="mailto:cost@averlynxstore.one">
                  cost@averlynxstore.one
                </a>
                . Include an order number when the question concerns a purchase,
                but do not send passwords or full payment-card details.
              </p>
            </section>

            <aside className="legal-page__notice">
              These terms and the policies expressly incorporated into them form
              the agreement for use of the store. Mandatory consumer law always
              takes priority over a conflicting term.
            </aside>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}

export default Terms;
